import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { ApiCall } from "../helper/axios";
import { toast } from "../common_components/Toast/ToastUtils";
// ✅ Async Thunks
export const fetchMarketingCalendar = createAsyncThunk("marketingCalendar/fetchList", async (params, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/marketing-calendar-list", params, {
            Authorization: token || ""
        });
        if (response && 'data' in response && response.data?.success) {
            return {
                ...response.data,
                params
            };
        }
        else {
            return rejectWithValue("Failed to fetch marketing calendar");
        }
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "An error occurred");
    }
});
export const createProposal = createAsyncThunk("marketingCalendar/createProposal", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/marketing-calendar/create-proposal", payload, {
            Authorization: token || ""
        });
        if (response && 'data' in response && response.data?.success) {
            return response.data;
        }
        else {
            return rejectWithValue("Failed to create proposal");
        }
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "Create proposal failed");
    }
});
export const updateProposal = createAsyncThunk("marketingCalendar/updateProposal", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/marketing-calendar/update-proposal", payload, {
            Authorization: token || ""
        });
        if (response && 'data' in response && response.data?.success) {
            return response.data;
        }
        else {
            return rejectWithValue("Failed to update proposal");
        }
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "Update proposal failed");
    }
});
export const deleteProposal = createAsyncThunk("marketingCalendar/deleteProposal", async ({ proposalId }, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("DELETE", `/api/marketing-calendar/proposal/${proposalId}`, {}, {
            Authorization: token || ""
        });
        if (response && 'data' in response && response.data?.success) {
            return response.data;
        }
        else {
            return rejectWithValue("Failed to delete proposal");
        }
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "Delete proposal failed");
    }
});
export const createApprovalActivity = createAsyncThunk("marketingCalendar/createApprovalActivity", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const { hideToast, module, ...apiPayload } = payload;
        const headers = { Authorization: token || "" };
        if (module)
            headers["x-module-key"] = module;
        const response = await ApiCall("POST", "/api/approval-activity", apiPayload, headers);
        if (response && "data" in response && response.data?.success) {
            return response.data;
        }
        else {
            return rejectWithValue("Failed to submit approval activity");
        }
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "Approval activity failed");
    }
});
export const getApprovalActivity = createAsyncThunk("marketingCalendar/getApprovalActivity", async (params, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const headers = { Authorization: token || "" };
        if (params.module)
            headers["x-module-key"] = params.module;
        let url = `/api/approval-activity?campaign_id=${params.campaign_id}`;
        if (params.client_id) {
            url += `&client_id=${params.client_id}`;
        }
        const response = await ApiCall("GET", url, {}, headers);
        if (response && "data" in response && response.data?.success) {
            return response.data.data;
        }
        else {
            return rejectWithValue("Failed to fetch approval activity");
        }
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "Approval history failed");
    }
});
export const refreshMarketingCalendar = createAsyncThunk("marketingCalendar/refresh", async (_, { dispatch, getState }) => {
    const { marketingCalendar } = getState();
    if (marketingCalendar.lastParams) {
        dispatch(fetchMarketingCalendar(marketingCalendar.lastParams));
    }
});
// ✅ Monday Test - Get Boards
export const fetchMondayBoards = createAsyncThunk("marketingCalendar/fetchMondayBoards", async (_, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("GET", "/monday-test", {}, {
            Authorization: token || ""
        });
        if (response && "data" in response && response.data?.success) {
            return response.data.data;
        }
        else {
            return rejectWithValue("Failed to fetch Monday boards");
        }
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "Monday boards fetch failed");
    }
});
// ✅ Monday Sync - Full Sync (Updated)
export const syncMondayTask = createAsyncThunk("marketingCalendar/syncMondayTask", async (payload, { getState, rejectWithValue }) => {
    try {
        const { auth } = getState();
        const token = auth?.token;
        const response = await ApiCall("POST", "/api/monday-sync", payload, {
            Authorization: token || ""
        });
        if (response && "data" in response && response.data?.success) {
            return response.data;
        }
        else {
            return rejectWithValue("Failed to sync Monday task");
        }
    }
    catch (error) {
        return rejectWithValue(error?.response?.data?.message ||
            error.message ||
            "Monday sync failed");
    }
});
// ✅ Initial State
const initialState = {
    data: null,
    loading: false,
    error: null,
    lastUpdated: null,
    approvalActivity: [],
    approvalActivityLoading: false,
    lastParams: null,
    mondayBoards: null,
    mondayBoardsLoading: false,
    mondaySyncLoading: false,
    currentFetchRequestId: null
};
// ✅ Slice
const marketingCalendarSlice = createSlice({
    name: "marketingCalendar",
    initialState,
    reducers: {
        clearMarketingCalendarData: (state) => {
            state.data = null;
            state.lastUpdated = null;
            state.error = null;
        },
        clearMarketingCalendarError: (state) => {
            state.error = null;
        },
        clearMondayBoards: (state) => {
            state.mondayBoards = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // FETCH MARKETING CALENDAR
            // Only the most recently dispatched request is allowed to write to
            // state — otherwise an older, slower request resolving after a
            // newer one can overwrite fresh data with stale data (e.g. rapid
            // month navigation, or an action-triggered refresh racing with the
            // effect-driven fetch).
            .addCase(fetchMarketingCalendar.pending, (state, action) => {
            state.loading = true;
            state.error = null;
            state.currentFetchRequestId = action.meta.requestId;
        })
            .addCase(fetchMarketingCalendar.fulfilled, (state, action) => {
            if (action.meta.requestId !== state.currentFetchRequestId)
                return;
            state.loading = false;
            state.data = action.payload.data;
            state.lastUpdated = action.payload.data?.lastUpdated || action.payload.lastUpdated || null;
            state.lastParams = action.payload.params;
            state.error = null;
        })
            .addCase(fetchMarketingCalendar.rejected, (state, action) => {
            if (action.meta.requestId !== state.currentFetchRequestId)
                return;
            state.loading = false;
            state.lastUpdated = null;
            state.error = action.payload || "Failed to fetch marketing calendar";
        })
            // CREATE PROPOSAL
            .addCase(createProposal.fulfilled, (state, action) => {
            if (!action.meta.arg.proposal?.hideToast) {
                toast.success("Proposal created successfully");
            }
        })
            .addCase(createProposal.rejected, (_, action) => {
            if (!action.meta.arg.proposal?.hideToast) {
                toast.error(action.payload || "Failed to create proposal");
            }
        })
            // UPDATE PROPOSAL
            .addCase(updateProposal.fulfilled, (state, action) => {
            if (!action.meta.arg.proposal?.hideToast) {
                toast.success("Proposal updated successfully");
            }
        })
            .addCase(updateProposal.rejected, (_, action) => {
            if (!action.meta.arg.proposal?.hideToast) {
                toast.error(action.payload || "Failed to update proposal");
            }
        })
            // DELETE PROPOSAL
            .addCase(deleteProposal.fulfilled, (state) => {
            toast.success("Proposal and history deleted successfully");
        })
            .addCase(deleteProposal.rejected, (_, action) => {
            toast.error(action.payload || "Failed to delete proposal");
        })
            // GET APPROVAL ACTIVITY
            .addCase(getApprovalActivity.pending, (state) => {
            state.approvalActivityLoading = true;
        })
            .addCase(getApprovalActivity.fulfilled, (state, action) => {
            state.approvalActivityLoading = false;
            state.approvalActivity = action.payload;
        })
            .addCase(getApprovalActivity.rejected, (state) => {
            state.approvalActivityLoading = false;
        })
            // CREATE APPROVAL ACTIVITY
            .addCase(createApprovalActivity.fulfilled, (state, action) => {
            if (!action.meta.arg.hideToast) {
                toast.success("Status updated successfully");
            }
        })
            .addCase(createApprovalActivity.rejected, (_, action) => {
            if (!action.meta.arg.hideToast) {
                toast.error(action.payload || "Failed to update status");
            }
        })
            // ✅ MONDAY - FETCH BOARDS
            .addCase(fetchMondayBoards.pending, (state) => {
            state.mondayBoardsLoading = true;
        })
            .addCase(fetchMondayBoards.fulfilled, (state, action) => {
            state.mondayBoardsLoading = false;
            state.mondayBoards = action.payload;
        })
            .addCase(fetchMondayBoards.rejected, (state, action) => {
            state.mondayBoardsLoading = false;
            toast.error(action.payload || "Failed to fetch Monday boards");
        })
            // ✅ MONDAY - SYNC TASK
            .addCase(syncMondayTask.pending, (state) => {
            state.mondaySyncLoading = true;
        })
            .addCase(syncMondayTask.fulfilled, (state, action) => {
            state.mondaySyncLoading = false;
            if (!action.meta.arg.hideToast) {
                const isNew = action.payload?.data?.item?.isNew;
                toast.success(isNew ? "Task created in Monday" : "Task updated in Monday");
            }
        })
            .addCase(syncMondayTask.rejected, (state, action) => {
            state.mondaySyncLoading = false;
            if (!action.meta.arg.hideToast) {
                toast.error(action.payload || "Failed to sync Monday task");
            }
        });
    },
});
// ✅ Actions
export const { clearMarketingCalendarData, clearMarketingCalendarError, clearMondayBoards } = marketingCalendarSlice.actions;
// ✅ Selectors
export const selectMarketingCalendarData = (state) => state.marketingCalendar.data;
export const selectMarketingCalendarLoading = (state) => state.marketingCalendar.loading;
export const selectMarketingCalendarError = (state) => state.marketingCalendar.error;
export const selectMarketingCalendarStats = (state) => state.marketingCalendar.data?.stats;
export const selectMarketingCalendarTabs = (state) => state.marketingCalendar.data?.tabs;
export const selectMarketingCalendarLastUpdated = (state) => state.marketingCalendar.lastUpdated;
export const selectApprovalActivity = (state) => state.marketingCalendar.approvalActivity;
export const selectApprovalActivityLoading = (state) => state.marketingCalendar.approvalActivityLoading;
// ✅ Monday Selectors
export const selectMondayBoards = (state) => state.marketingCalendar.mondayBoards;
export const selectMondayBoardsLoading = (state) => state.marketingCalendar.mondayBoardsLoading;
export const selectMondaySyncLoading = (state) => state.marketingCalendar.mondaySyncLoading;
export default marketingCalendarSlice.reducer;
